import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "relative z-50 flex justify-end bg-white pt-4 pb-2" }
const _hoisted_2 = { class: "flex h-full flex-col items-center" }
const _hoisted_3 = { class: "pointer-events-auto relative flex w-full flex-col items-center gap-2.5 rounded-lg p-6" }
const _hoisted_4 = { class: "pointer-events-auto absolute top-4 right-4" }
const _hoisted_5 = { class: "relative h-14 w-14 overflow-hidden rounded-full border border-gray-200" }
const _hoisted_6 = { class: "flex h-full w-full items-center justify-center bg-gray-100" }
const _hoisted_7 = { class: "flex flex-col items-center gap-1" }
const _hoisted_8 = { class: "text-base font-bold text-black" }
const _hoisted_9 = { class: "flex items-center justify-center rounded-[14px] bg-orange-100 px-2 py-1" }
const _hoisted_10 = { class: "text-2xs text-primary" }
const _hoisted_11 = {
  key: 0,
  class: "text-2xs text-black"
}
const _hoisted_12 = { class: "pointer-events-auto flex w-full flex-col gap-2" }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = { class: "flex items-center justify-center" }
const _hoisted_15 = { class: "text-sm font-medium" }

import { profileService } from '@/services';
import { useAuthStore } from '@/store/auth';
import { useUserStore } from '@/store/user';
import type { ProfileData } from '@/types/profile';
import { IonContent, IonIcon, IonImg, IonMenu, menuController } from '@ionic/vue';
import { peopleOutline, personCircleOutline, settingsOutline } from 'ionicons/icons';
import { X } from 'lucide-vue-next';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useBrowser } from '@/composables/useBrowser';
import { cmsService } from '@/services/CmsService';
import BaseButton from '../buttons/BaseButton.vue';

    interface MenuItem {
        label: string;
        image?: string;
        icon?: unknown;
        route?: string;
        external?: boolean;
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'MobileSidePanel',
  setup(__props) {

    const router = useRouter();
    const route = useRoute();
    const userStore = useUserStore();
    const authStore = useAuthStore();
    const { openUrl } = useBrowser();
    const joinCommunityLink = ref<string | null>(null);

    // Profile data from API (same as ProfileIndexPage)
    const profileData = ref<ProfileData | null>(null);

    // Local reactive state for immediate UI updates
    const displayUserName = ref(
        authStore.user?.firstName && authStore.user?.lastName
            ? `${authStore.user.firstName} ${authStore.user.lastName}`
            : authStore.user?.firstName || 'Guest',
    );

    // Watch for user changes and update immediately
    watch(
        () => authStore.user,
        newUser => {
            displayUserName.value =
                newUser?.firstName && newUser?.lastName
                    ? `${newUser.firstName} ${newUser.lastName}`
                    : newUser?.firstName || 'Guest';
        },
        { immediate: true, deep: true },
    );

    // Computed property as fallback
    const userName = computed(() => displayUserName.value);

    // Computed property to check if user is a member (has loyaltyMemberId)
    const isMember = computed(() => {
        return !!authStore.user?.loyaltyMemberId;
    });

    // Computed property for user profile picture - uses same source as ProfileIndexPage
    const userProfilePicture = computed(() => {
        return profileData.value?.profilePicture || profileData.value?.image || null;
    });

    // Fetch profile data (same as ProfileIndexPage)
    const fetchProfileData = async () => {
        if (!authStore.isAuthenticated) return;
        if (authStore.isInVerificationProcess) return;

        try {
            const response = await profileService.getProfileEdit();
            profileData.value = response.data;
        } catch (error) {
            console.error('Failed to fetch profile data:', error);
        }
    };

    onMounted(async () => {
        // Load the Join Community link from the CMS (slug: join_link)
        try {
            const page = await cmsService.getPage('join_link');
            joinCommunityLink.value = page.content?.section1_link ?? null;
        } catch {
            // Leave the link empty; the menu item becomes a no-op when unavailable.
            joinCommunityLink.value = null;
        }
    });

    const menuItems: MenuItem[] = [
        {
            label: 'Dashboard',
            image: '/assets/images/icons/chart.png',
            route: '/home',
        },
        {
            label: 'Loans',
            image: '/assets/images/icons/money.png',
            route: '/home/loans',
        },
        {
            label: 'Rewards',
            image: '/assets/images/icons/gift.png',
            route: '/rewards',
        },
        {
            label: 'Refer & Earn',
            image: '/assets/images/icons/link.png',
            route: '/home/refer-and-earn',
        },
        {
            label: 'My Motorcycle',
            image: '/assets/images/icons/motor.png',
            route: '/motorcycles/my-motorcycles',
        },
        {
            label: 'Transactions',
            image: '/assets/images/icons/file.png',
            route: '/home/transactions',
        },
        {
            label: 'Notifications',
            icon: '/assets/nav/ic-notifications.svg',
            route: '/notifications',
        },
        {
            label: 'Chat',
            icon: '/assets/nav/ic-chat.svg',
            route: '/chat',
        },
        {
            label: 'Promos & Advisories',
            image: '/assets/images/icons/megaphone.png',
            route: '/home/promos',
        },
        {
            label: 'Browse Motorcycle',
            image: '/assets/images/icons/motor.png',
            route: '/motorcycles',
        },
        {
            label: 'Loan Calculator',
            image: '/assets/images/icons/calculator.png',
            route: '/home/calculator',
        },
        {
            label: 'Branch Locator',
            image: '/assets/images/icons/map.png',
            route: '/home/branch-locator',
        },
        {
            label: 'Join Kaibigan Community',
            icon: peopleOutline,
            external: true,
        },
    ];

    const navigateTo = async (item: MenuItem) => {
        if (item.external) {
            if (joinCommunityLink.value) {
                await openUrl(joinCommunityLink.value);
                menuController.close('mobile-side-panel');
            }
            // No-op if the CMS link isn't loaded yet.
            return;
        }

        if (item.route) {
            router.push(item.route);
            menuController.close('mobile-side-panel');
        }
    };

    const navigateToProfile = () => {
        router.push('/home/profile');
        menuController.close('mobile-side-panel');
    };

    const isActive = (path: string) => {
        return route.path === path;
    };

    const closeMenu = async () => {
        await menuController.close('mobile-side-panel');
    };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonMenu), {
    "menu-id": "mobile-side-panel",
    "content-id": "main-content",
    class: "mobile-menu",
    side: "end",
    "swipe-gesture": false
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(BaseButton, {
          fill: "clear",
          size: "small",
          class: "close-button pointer-events-auto cursor-pointer",
          onClick: closeMenu
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(X), { class: "pointer-events-none size-6 text-primary" })
          ]),
          _: 1
        })
      ]),
      _createVNode(_unref(IonContent), { class: "ion-padding bg-white" }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createVNode(BaseButton, {
                  class: "settings-button flex items-center justify-center rounded-full p-1 transition-colors hover:bg-gray-100",
                  fill: "clear",
                  onClick: navigateToProfile
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(IonIcon), {
                      icon: _unref(settingsOutline),
                      class: "text-base text-black"
                    }, null, 8, ["icon"])
                  ]),
                  _: 1
                })
              ]),
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  (userProfilePicture.value)
                    ? (_openBlock(), _createBlock(_unref(IonImg), {
                        key: 0,
                        src: userProfilePicture.value,
                        class: "h-full w-full object-cover"
                      }, null, 8, ["src"]))
                    : (_openBlock(), _createBlock(_unref(IonIcon), {
                        key: 1,
                        icon: _unref(personCircleOutline),
                        class: "text-4xl text-gray-400"
                      }, null, 8, ["icon"]))
                ])
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("span", _hoisted_8, _toDisplayString(userName.value), 1),
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("span", _hoisted_10, _toDisplayString(isMember.value ? 'Member' : 'Non-Member'), 1)
                ]),
                (_unref(authStore).user?.loyaltyMemberId)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(_unref(authStore).user.loyaltyMemberId), 1))
                  : _createCommentVNode("", true)
              ])
            ]),
            _cache[0] || (_cache[0] = _createElementVNode("div", { class: "w-full py-2" }, [
              _createElementVNode("span", { class: "text-xs text-black" }, "Menu")
            ], -1)),
            _createElementVNode("div", _hoisted_12, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(menuItems, (item, index) => {
                return _createElementVNode("div", {
                  key: index,
                  class: _normalizeClass(["pointer-events-auto flex cursor-pointer items-center gap-3 rounded-lg p-3 transition-colors", 
                            isActive(item.route)
                                ? 'bg-orange-50 text-primary'
                                : 'text-black hover:bg-gray-50'
                        ]),
                  onClick: ($event: any) => (navigateTo(item))
                }, [
                  _createElementVNode("div", _hoisted_14, [
                    (item.image)
                      ? (_openBlock(), _createBlock(_unref(IonImg), {
                          key: 0,
                          src: item.image,
                          class: "h-5 w-5 object-contain"
                        }, null, 8, ["src"]))
                      : (_openBlock(), _createBlock(_unref(IonIcon), {
                          key: 1,
                          icon: item.icon,
                          class: _normalizeClass(["text-xl", isActive(item.route) ? 'text-primary' : 'text-black'])
                        }, null, 8, ["icon", "class"]))
                  ]),
                  _createElementVNode("span", _hoisted_15, _toDisplayString(item.label), 1)
                ], 10, _hoisted_13)
              }), 64))
            ])
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})